import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AdminUser {
  user_id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  credits: {
    monthly_credits: number;
    monthly_used: number;
    topup_credits: number;
    monthly_reset_at: string;
    topup_expires_at: string | null;
  } | null;
  roles: string[];
  organizations: { org_name: string; org_role: string }[];
}

export interface AdminMetrics {
  total_users: number;
  total_runs: number;
  new_users: number;
  total_credits_allocated: number;
  total_credits_used: number;
  total_topup_credits: number;
  transactions_by_source: Record<string, number>;
}

export interface ChartDataPoint {
  timestamp: string;
  runs: number;
  credits_consumed: number;
  topup_purchased: number;
  new_users: number;
}

export type TimeframePreset = "60m" | "24h" | "7d" | "30d" | "90d" | "custom";

async function adminFetch(action: string, method = "GET", body?: unknown, params?: Record<string, string>) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  const queryParams = new URLSearchParams({ action, ...params });
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-api?${queryParams}`;
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export function useAdminData() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);

  const getTimeParams = useCallback((timeframe: TimeframePreset, customFrom?: string, customTo?: string): Record<string, string> => {
    if (timeframe === "custom" && customFrom) {
      const params: Record<string, string> = { from: customFrom };
      if (customTo) params.to = customTo;
      return params;
    }
    return { timeframe };
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetch("users");
      setUsers(data.users || []);
    } catch (e) {
      console.error("Failed to fetch users:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMetrics = useCallback(async (timeframe: TimeframePreset = "30d", customFrom?: string, customTo?: string) => {
    try {
      const params = getTimeParams(timeframe, customFrom, customTo);
      const data = await adminFetch("metrics", "GET", undefined, params);
      setMetrics(data);
    } catch (e) {
      console.error("Failed to fetch metrics:", e);
    }
  }, [getTimeParams]);

  const fetchChartData = useCallback(async (timeframe: TimeframePreset = "30d", customFrom?: string, customTo?: string) => {
    setChartLoading(true);
    try {
      const params = getTimeParams(timeframe, customFrom, customTo);
      const data = await adminFetch("chart-data", "GET", undefined, params);
      setChartData(data.series || []);
    } catch (e) {
      console.error("Failed to fetch chart data:", e);
    } finally {
      setChartLoading(false);
    }
  }, [getTimeParams]);

  const addCredits = useCallback(async (userId: string, amount: number) => {
    return adminFetch("add-credits", "POST", { user_id: userId, amount });
  }, []);

  const setMonthlyCredits = useCallback(async (userId: string, monthlyCredits: number) => {
    return adminFetch("set-monthly-credits", "POST", { user_id: userId, monthly_credits: monthlyCredits });
  }, []);

  const setRole = useCallback(async (userId: string, role: string, remove = false) => {
    return adminFetch("set-role", "POST", { user_id: userId, role, remove });
  }, []);

  const expireTopups = useCallback(async () => {
    return adminFetch("expire-topups", "POST");
  }, []);

  return {
    users, metrics, chartData, loading, chartLoading,
    fetchUsers, fetchMetrics, fetchChartData,
    addCredits, setMonthlyCredits, setRole, expireTopups,
  };
}
